import logger from '../../utils/logger';
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, MailOpen, Mail, Trash2, CheckCircle, XCircle, Info } from 'lucide-react';
import { useAuth } from '../../pages/AuthContext';
import api from '../../utils/api';

const NotificationBell = ({ onNavigate }) => {
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const [previousUnreadCount, setPreviousUnreadCount] = useState(0);
  const { currentUser } = useAuth();

  // Función para reproducir sonido de campana
  const playNotificationSound = useCallback(() => {
    try {
      // Crear AudioContext
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const currentTime = audioContext.currentTime;

      // Función para crear una nota de campana con armónicos
      const createBellNote = (frequency, startTime, duration) => {
        // Oscilador principal
        const osc1 = audioContext.createOscillator();
        const gain1 = audioContext.createGain();

        // Armónicos para dar efecto de campana (más brillante y metálico)
        const osc2 = audioContext.createOscillator();
        const gain2 = audioContext.createGain();

        const osc3 = audioContext.createOscillator();
        const gain3 = audioContext.createGain();

        // Conectar todos los osciladores
        osc1.connect(gain1);
        osc2.connect(gain2);
        osc3.connect(gain3);

        gain1.connect(audioContext.destination);
        gain2.connect(audioContext.destination);
        gain3.connect(audioContext.destination);

        // Configurar frecuencias (fundamental + armónicos)
        osc1.frequency.setValueAtTime(frequency, startTime);
        osc2.frequency.setValueAtTime(frequency * 2, startTime); // Octava superior
        osc3.frequency.setValueAtTime(frequency * 3, startTime); // Quinta armónica

        // Usar onda triangular para un sonido más suave
        osc1.type = 'triangle';
        osc2.type = 'sine';
        osc3.type = 'sine';

        // Configurar volúmenes (el fundamental más fuerte)
        gain1.gain.setValueAtTime(0.4, startTime);
        gain2.gain.setValueAtTime(0.2, startTime);
        gain3.gain.setValueAtTime(0.1, startTime);

        // Decay exponencial para simular campana (fade out largo)
        gain1.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
        gain2.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
        gain3.gain.exponentialRampToValueAtTime(0.01, startTime + duration);

        // Iniciar y detener
        osc1.start(startTime);
        osc2.start(startTime);
        osc3.start(startTime);

        osc1.stop(startTime + duration);
        osc2.stop(startTime + duration);
        osc3.stop(startTime + duration);
      };

      // Crear el "ding-dong" de la campana (dos notas)
      createBellNote(880, currentTime, 0.8);        // Primera nota (A5)
      createBellNote(660, currentTime + 0.15, 1.0); // Segunda nota (E5) - ligeramente después

      logger.debug('Sonido de campana reproducido');
    } catch (error) {
      logger.error('Error al reproducir sonido de notificación:', error);
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    if (!currentUser) return;

    setIsLoading(true);
    try {
      const response = await api.get(`/notifications/user/${currentUser.id}`);
      // Transform dates from ISO strings to Date objects
      const transformedData = response.data.map(n => ({
        ...n,
        date: new Date(n.created_at),
        read: n.read
      }));
      setNotifications(transformedData);
    } catch (error) {
      logger.error('Error fetching notifications:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchNotifications();

    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);

    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const unreadCount = notifications.filter(n => !n.read).length;
  const recentNotifications = notifications
    .sort((a, b) => b.date.getTime() - a.date.getTime())
    .slice(0, 10);

  // Detectar nuevas notificaciones y reproducir sonido
  useEffect(() => {
    // Solo reproducir sonido si aumentó el número de notificaciones no leídas
    // y no es la carga inicial (previousUnreadCount > 0 significa que ya hubo una carga previa)
    if (previousUnreadCount > 0 && unreadCount > previousUnreadCount) {
      logger.debug(`Nueva notificación detectada. Anterior: ${previousUnreadCount}, Actual: ${unreadCount}`);
      playNotificationSound();
    }

    // Actualizar el contador previo
    setPreviousUnreadCount(unreadCount);
  }, [unreadCount, previousUnreadCount, playNotificationSound]);

  const toggleReadStatus = async (id, currentReadStatus) => {
    try {
      const endpoint = currentReadStatus
        ? `/notifications/${id}/unread`
        : `/notifications/${id}/read`;

      await api.put(endpoint);
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, read: !currentReadStatus } : n)
      );
    } catch (error) {
      logger.error('Error toggling read status:', error);
    }
  };

  const markAllAsRead = async () => {
    if (!currentUser) return;

    try {
      await api.put(`/notifications/user/${currentUser.id}/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      logger.error('Error marking all as read:', error);
    }
  };

  const deleteNotification = async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (error) {
      logger.error('Error deleting notification:', error);
    }
  };

  const formatTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - date) / 1000);
    let interval = seconds / 31536000;
    if (interval > 1) return Math.floor(interval) + " años";
    interval = seconds / 2592000;
    if (interval > 1) return Math.floor(interval) + " meses";
    interval = seconds / 86400;
    if (interval > 1) return Math.floor(interval) + " días";
    interval = seconds / 3600;
    if (interval > 1) return Math.floor(interval) + " horas";
    interval = seconds / 60;
    if (interval > 1) return Math.floor(interval) + " minutos";
    return Math.floor(seconds) + " segundos";
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'reservation_approved':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'reservation_rejected':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'reservation_created':
        return <Bell className="w-5 h-5 text-blue-600" />;
      case 'payment_received':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      default:
        return <Info className="w-5 h-5 text-gray-600" />;
    }
  };

  const handleNotificationClick = async (notification) => {
    // Marcar como leída si no lo está
    if (!notification.read) {
      await toggleReadStatus(notification.id, notification.read);
    }

    // Cerrar el dropdown
    setShowDropdown(false);

    // Navegar al módulo correcto según el rol del usuario
    if (!onNavigate) return;

    // Determinar a qué módulo ir según el rol
    const userRole = currentUser?.role;

    // El ID de la reserva puede estar en diferentes lugares según el tipo de notificación
    // Para solicitudes de cambio: metadata.reservation_id
    // Para otras notificaciones: reference_id o metadata.reservation_id
    const reservationId = notification.metadata?.reservation_id || notification.reference_id;

    // Determinar la sección a abrir según el tipo de notificación
    let section = null;
    const notificationType = notification.type;

    // Para notificaciones de cambios, abrir en la sección de actividades
    const isChangeRequest = notificationType?.includes('change_request') ||
                           notificationType?.includes('solicitud') ||
                           notification.message?.toLowerCase().includes('solicitud de cambio') ||
                           notification.title?.toLowerCase().includes('solicitud de cambio');

    if (userRole === 'asesor') {
      // Los asesores van al módulo de Reservas
      onNavigate('reservations', reservationId, null);
    } else if (userRole === 'gestor' || userRole === 'administrador') {
      // Gestores y administradores van al módulo de Gestión
      // Si es una notificación de solicitud de cambio, abrir en la sección de actividades
      if (isChangeRequest) {
        section = 'history'; // Sección de actividades y cambios
      }
      onNavigate('gestion', reservationId, section);
    }
  };

  return (
    <div className="relative">
      <motion.button
        onClick={() => setShowDropdown(!showDropdown)}
        className={`relative p-3 rounded-full transition-all duration-300 ${
          unreadCount > 0
            ? 'bg-blue-900 text-white shadow-lg hover:shadow-xl hover:bg-blue-950'
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800'
        }`}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.95 }}
        animate={unreadCount > 0 ? {
          scale: [1, 1.05, 1],
        } : {}}
        transition={unreadCount > 0 ? {
          scale: {
            duration: 2,
            repeat: Infinity,
            repeatType: "reverse"
          }
        } : {}}
      >
        <motion.div
          animate={unreadCount > 0 ? {
            rotate: [0, -15, 15, -15, 0],
          } : {}}
          transition={unreadCount > 0 ? {
            duration: 0.5,
            repeat: Infinity,
            repeatDelay: 3
          } : {}}
        >
          <Bell className="w-7 h-7" />
        </motion.div>
        {unreadCount > 0 && (
          <motion.span
            key={unreadCount}
            className="absolute -top-1 -right-1 min-w-[24px] h-6 px-1.5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center border-2 border-white shadow-lg"
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 500, damping: 30 }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </motion.span>
        )}
      </motion.button>

      <AnimatePresence>
        {showDropdown && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50"
          >
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-bold text-gray-800">Notificaciones Recientes</h3>
              {unreadCount > 0 && (
                <motion.button
                  onClick={markAllAsRead}
                  className="text-blue-600 text-sm hover:underline"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Marcar todas como leídas
                </motion.button>
              )}
            </div>
            <ul className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
              {isLoading ? (
                <li className="p-4 text-center text-gray-500">Cargando...</li>
              ) : recentNotifications.length > 0 ? (
                recentNotifications.map(notification => (
                  <motion.li
                    key={notification.id}
                    className={`p-4 hover:bg-gray-50 transition-colors duration-150 ${notification.read ? 'bg-white' : 'bg-blue-50'}`}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="flex items-start justify-between">
                      <div
                        className="flex items-start gap-2 flex-1 cursor-pointer"
                        onClick={() => handleNotificationClick(notification)}
                      >
                        {getNotificationIcon(notification.type)}
                        <div className="flex-1">
                          <p className={`font-semibold text-sm ${notification.read ? 'text-gray-800' : 'text-blue-800'}`}>
                            {notification.title}
                          </p>
                          <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                          <p className="text-xs text-gray-400 mt-1">{formatTimeAgo(notification.date)} atrás</p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 ml-2">
                        <motion.button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleReadStatus(notification.id, notification.read);
                          }}
                          className="p-1 rounded-full text-gray-400 hover:text-blue-600 hover:bg-gray-100"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          title={notification.read ? "Marcar como no leída" : "Marcar como leída"}
                        >
                          {notification.read ? <MailOpen className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
                        </motion.button>
                        <motion.button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteNotification(notification.id);
                          }}
                          className="p-1 rounded-full text-gray-400 hover:text-red-600 hover:bg-gray-100"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </motion.button>
                      </div>
                    </div>
                  </motion.li>
                ))
              ) : (
                <li className="p-4 text-center text-gray-500">No hay notificaciones.</li>
              )}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationBell;
